import React, { useEffect, useState, useRef } from 'react';
import { DECAY_FACTOR, calculateY, timestampToPosition } from './utils';

interface ExponentialCurveProps {
  start: number;
  finish: number;
  dashed?: boolean;
}

const ExponentialCurve: React.FC<ExponentialCurveProps> = ({
  start,
  finish,
  dashed = true,
}) => {
  const [timestamp, setTimestamp] = useState<number>(Date.now() / 1000);
  const svgRef = useRef<SVGSVGElement>(null);
  const [svgDimensions, setSvgDimensions] = useState<{
    width: number;
    height: number;
  }>({ width: 0, height: 0 });
  const [path, setPath] = useState<string>('');
  const [dotPosition, setDotPosition] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });

  useEffect(() => {
    const updateTimestamp = () => {
      setTimestamp(Date.now() / 1000);
    };

    const intervalId = setInterval(updateTimestamp, 1000); // Update timestamp every second

    return () => {
      clearInterval(intervalId); // Clean up the interval on component unmount
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setSvgDimensions(getSvgDimensions());
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const dimensions = getSvgDimensions();
    setSvgDimensions(dimensions);

    const { width, height } = dimensions;
    // the <circle> is ~10% in size so just add this as padding so we dont clip
    const startX = width * 0.1;
    const startY = height * 0.1;
    const endX = width * 0.9;
    const endY = height * 0.9;

    const decayFactor =
      Math.log(endY / (endY * DECAY_FACTOR)) / (endX - startX);

    let pathData = `M ${startX} ${calculateY(
      startX,
      startX,
      startY,
      endY,
      decayFactor
    )}`;

    for (let i = startX + 1; i <= endX; i++) {
      const x = i;
      const y = calculateY(i, startX, startY, endY, decayFactor);
      pathData += ` L ${x} ${y}`;
    }

    const dotPosition = timestampToPosition(
      timestamp,
      start,
      finish,
      startX,
      startY,
      endX,
      endY
    );

    // Update the state with the new dimensions and coordinates
    setPath(pathData);
    setDotPosition(dotPosition);
  }, [timestamp, start, finish, svgDimensions.width, svgDimensions.height]);

  const getSvgDimensions = (): { width: number; height: number } => {
    if (svgRef.current) {
      const { width, height } = svgRef.current.getBoundingClientRect();
      return { width, height };
    }
    return { width: 0, height: 0 };
  };

  const circleRadius = svgDimensions.width * 0.05; // Adjust the circle size as a percentage of the SVG width

  return (
    <svg
      ref={svgRef}
      width="100%"
      height="100%"
      viewBox={`0 0 ${svgDimensions.width} ${svgDimensions.height}`}
    >
      <g>
        <path
          d={path}
          fill="none"
          stroke={'#E72C62'}
          strokeWidth={'1%'}
          strokeDasharray={dashed ? '4 2' : undefined}
        />
        <circle
          cx={dotPosition.x}
          cy={dotPosition.y}
          r={circleRadius}
          fill={'#E72C62'}
        />
        <circle
          cx={dotPosition.x}
          cy={dotPosition.y}
          r={circleRadius * 1.3} // Increase the size of the outer circle by 30%
          fill="none"
          stroke={'#F28DAA'}
          strokeWidth={circleRadius * 0.8} // Adjust the stroke width as a percentage of the circle radius
        />
      </g>
    </svg>
  );
};

export default ExponentialCurve;
